import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Check, Package, Eye, EyeOff, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { API, saveTokens, formatApiErrorDetail } from "@/lib/api";
import { TERMS_VERSION } from "@/lib/termsVersion";

export function AuthPage({ onLogin, onShowPrivacy, onShowTerms, onStartTutorial }) {
  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && !forgotMode && !agreeTerms) {
      setError("Please agree to the Terms of Service to create an account.");
      return;
    }
    setLoading(true);
    try {
      if (forgotMode) {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setForgotSent(true);
        setError("");
        setLoading(false);
        return void data;
      }
      if (isLogin) {
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) { deviceId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem('device_id', deviceId); }
        const { data } = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe, device_id: deviceId });
        saveTokens(data.access_token, data.refresh_token);
        if (data.restored) toast.success("Welcome back! Your account and all its data have been restored.");
        onLogin(data);
      } else {
        let regDeviceId = localStorage.getItem('device_id');
        if (!regDeviceId) { regDeviceId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem('device_id', regDeviceId); }
        const { data } = await axios.post(`${API}/auth/register`, { email, password, name: name || undefined, device_id: regDeviceId });
        localStorage.setItem('terms_notice_seen', TERMS_VERSION);
        saveTokens(data.access_token, data.refresh_token);
        onLogin(data);
      }
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (err.response?.status === 401) {
        setError("Invalid email or password");
      } else if (!err.response) {
        setError("Cannot connect to server (" + (err.message || "network error") + "). Try refreshing the page.");
      } else {
        const body = typeof err.response?.data === 'string' ? err.response.data.substring(0, 100) : JSON.stringify(err.response?.data || {}).substring(0, 100);
        setError("Error " + (err.response?.status || "?") + ": " + body);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4" data-testid="auth-page">
      <Card className="w-full max-w-md border border-zinc-200 rounded-none shadow-none">
        <CardHeader className="p-6 pb-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Package className="w-10 h-10 text-orange-600" strokeWidth={2} />
            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              Pallet Cut Calculator
            </h1>
          </div>
          <p className="text-sm text-zinc-500">{forgotMode ? "Enter your email to get a reset link" : isLogin ? "Sign in to your account" : "Create a new account"}</p>
          {onStartTutorial && !forgotMode && (
            <button type="button" onClick={onStartTutorial}
              className="mt-3 mx-auto flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 border-dashed border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors"
              data-testid="auth-tutorial-btn">
              <PlayCircle className="w-4 h-4" />
              See How The App Works
            </button>
          )}
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1 rounded-none border-zinc-300"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1 rounded-none border-zinc-300"
                data-testid="auth-email-input"
              />
            </div>
            {!forgotMode && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  minLength={4}
                  className="rounded-none border-zinc-300 pr-10"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            )}
            {!isLogin && !forgotMode && (
              <div className="flex items-start gap-2 cursor-pointer select-none" data-testid="agree-terms-label">
                <div
                  className={`w-4 h-4 mt-0.5 flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                    agreeTerms ? 'bg-orange-600 border-orange-600' : 'border-zinc-300 hover:border-zinc-400'
                  }`}
                  onClick={() => setAgreeTerms(!agreeTerms)}
                  data-testid="agree-terms-checkbox"
                >
                  {agreeTerms && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-zinc-600">
                  I agree to the{" "}
                  <button type="button" onClick={onShowTerms} className="text-orange-600 hover:underline font-medium" data-testid="agree-terms-tos-link">Terms of Service</button>
                  {" "}and{" "}
                  <button type="button" onClick={onShowPrivacy} className="text-orange-600 hover:underline font-medium" data-testid="agree-terms-privacy-link">Privacy Policy</button>
                </span>
              </div>
            )}
            {isLogin && !forgotMode && (
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                <div
                  className={`w-4 h-4 border-2 flex items-center justify-center transition-colors ${
                    rememberMe ? 'bg-orange-600 border-orange-600' : 'border-zinc-300 hover:border-zinc-400'
                  }`}
                  onClick={() => setRememberMe(!rememberMe)}
                  data-testid="remember-me-checkbox"
                >
                  {rememberMe && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-zinc-600">Remember me for 1 year</span>
              </label>
            )}
            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
            {forgotMode && forgotSent && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 p-2" data-testid="forgot-sent-msg">
                If an account exists with that email, a reset link is on its way. Check your inbox (and spam folder). The link expires in 1 hour.
              </p>
            )}
            <Button
              type="submit"
              disabled={loading || (forgotMode && forgotSent)}
              className="w-full rounded-none bg-orange-600 hover:bg-orange-700 text-white font-semibold uppercase tracking-wider"
              data-testid="auth-submit-btn"
            >
              {loading ? "..." : forgotMode ? "Send Reset Link" : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>
          {isLogin && !forgotMode && (
            <div className="mt-3 text-center">
              <button type="button" onClick={() => { setForgotMode(true); setForgotSent(false); setError(""); }}
                className="text-xs text-zinc-500 hover:text-zinc-700 underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>
          )}
          <div className="mt-4 text-center">
            <button
              onClick={() => { if (forgotMode) { setForgotMode(false); setForgotSent(false); } else { setIsLogin(!isLogin); } setError(""); }}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              data-testid="auth-toggle-btn"
            >
              {forgotMode ? "Back to sign in" : isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
            <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-zinc-400">
              {onShowPrivacy && (
                <button type="button" onClick={onShowPrivacy} className="hover:text-zinc-600 transition-colors" data-testid="auth-privacy-link">
                  Privacy Policy
                </button>
              )}
              {onShowPrivacy && onShowTerms && <span>•</span>}
              {onShowTerms && (
                <button type="button" onClick={onShowTerms} className="hover:text-zinc-600 transition-colors" data-testid="auth-terms-link">
                  Terms of Service
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
